
import {
    Order,
    Driver,
    ServiceTimeMetricsQuery,
    ServiceTimeMetricsSnapshot,
    ServiceTimeObservation
} from '../../../types';
import {
    COLD_START_MINS_PER_KM,
    minPerKmToSpeedKmh,
    MIN_LEARNED_SPEED_KMH,
    MAX_LEARNED_SPEED_KMH
} from '../constants';

/**
 * Recalcula o fator de velocidade relativo e a VELOCIDADE MÉDIA REAL (km/h)
 * v7.5.1: Mapeamento Genético do Piloto com Proteção de Divisão por Zero.
 */
export const recalibrateDriverSpeed = (
    driver: Driver,
    order: Order
): { speedFactor: number, avgSpeedKmH: number } => {
    const journeyStart = order.departedHubAt || order.pickupConfirmedAt || order.statusUpdatedAt;

    // Fallback de segurança: Agora sincronizado com a constante universal (2.8 min/km ~= 21.4 km/h)
    const FALLBACK_SPEED = minPerKmToSpeedKmh(COLD_START_MINS_PER_KM);

    // Se faltam dados críticos para o cálculo, mantém o atual ou o fallback
    if (!order.deliveredAt || !journeyStart || !order.distance || order.distance === 0) {
        return {
            speedFactor: driver.speedFactor || 1.0,
            avgSpeedKmH: driver.avgSpeedKmH || FALLBACK_SPEED
        };
    }

    // Clamp de tempo: Viagem mínima de 1 minuto para evitar outliers de telemetria
    const actualMins = Math.max(1, (order.deliveredAt - journeyStart) / 60000);

    // 1. CÁLCULO DE VELOCIDADE MÉDIA (KM/H)
    const actualHours = actualMins / 60;
    const currentTripSpeed = order.distance / actualHours;

    // 2. CÁLCULO DE FATOR RELATIVO (vs Google)
    // Usa a constante universal de cold start para comparação base
    const estimatedMins = order.estimatedDuration || (order.distance * COLD_START_MINS_PER_KM);
    const currentFactor = estimatedMins / actualMins;

    // 3. APRENDIZADO POR MÉDIA MÓVEL (EMA)
    // Alpha de 0.2: A cada entrega, a Sofia muda 20% da "opinião" dela sobre o piloto
    const alpha = 0.2;

    const oldFactor = driver.speedFactor || 1.0;
    const oldSpeed = driver.avgSpeedKmH || FALLBACK_SPEED;

    const newFactor = (currentFactor * alpha) + (oldFactor * (1 - alpha));
    const newSpeed = (currentTripSpeed * alpha) + (oldSpeed * (1 - alpha));

    return {
        speedFactor: Math.max(0.6, Math.min(1.8, newFactor)),
        avgSpeedKmH: Math.max(MIN_LEARNED_SPEED_KMH, Math.min(MAX_LEARNED_SPEED_KMH, newSpeed)) // Limites físicos centralizados
    };
};

/**
 * MOTOR DE APRENDIZADO DE TEMPO DE SERVIÇO (V4.9)
 */
const MIN_SERVICE_TIME_MINS = 0.5;
const MAX_SERVICE_TIME_MINS = 60;
const DEFAULT_FRESH_WINDOW_DAYS = 30;
const DEFAULT_STALE_AFTER_DAYS = 21;

const isWithinServiceTimeBounds = (value: number): boolean => {
    return Number.isFinite(value) && value > MIN_SERVICE_TIME_MINS && value < MAX_SERVICE_TIME_MINS;
};

export const resolveServiceLearningDriverId = (order: Order): string | undefined => {
    return order.driverId || order.completedDriverId;
};

const extractServiceTimeObservation = (order: Order): ServiceTimeObservation | null => {
    const isDelivered = order.status === 'delivered' || order.status === 'archived';
    if (!isDelivered) return null;

    const observedAt = order.deliveredAt || order.statusUpdatedAt || order.createdAt;

    if (order.serviceTimeMins && isWithinServiceTimeBounds(order.serviceTimeMins)) {
        return {
            orderId: order.id,
            driverId: resolveServiceLearningDriverId(order),
            serviceTimeMinutes: order.serviceTimeMins,
            observedAt,
            source: 'explicit_order_metric'
        };
    }

    if (!order.arrivedAt || !order.deliveredAt || order.deliveredAt <= order.arrivedAt) {
        return null;
    }

    const inferredServiceMins = (order.deliveredAt - order.arrivedAt) / 60000;
    if (!isWithinServiceTimeBounds(inferredServiceMins)) return null;

    return {
        orderId: order.id,
        driverId: resolveServiceLearningDriverId(order),
        serviceTimeMinutes: inferredServiceMins,
        observedAt,
        source: 'arrived_delivered_delta'
    };
};

const averageServiceTime = (observations: ServiceTimeObservation[]): number => {
    if (observations.length === 0) return 0;
    const total = observations.reduce((sum, observation) => sum + observation.serviceTimeMinutes, 0);
    return total / observations.length;
};

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const computeServiceTimeConfidence = (
    sampleCount: number,
    freshSampleCount: number,
    isStale: boolean
): number => {
    if (sampleCount === 0) return 0;

    const sampleScore = clamp01(sampleCount / 12);
    const freshnessScore = isStale
        ? 0.25
        : clamp01(freshSampleCount / 8);

    return clamp01((sampleScore * 0.65) + (freshnessScore * 0.35));
};

export const calculateServiceTimeMetrics = (
    orders: Order[],
    query: ServiceTimeMetricsQuery = {}
): ServiceTimeMetricsSnapshot => {
    const now = query.nowMs ?? Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const monthMs = 30 * dayMs;
    const freshWindowMs = (query.freshWindowDays ?? DEFAULT_FRESH_WINDOW_DAYS) * dayMs;
    const staleAfterMs = (query.staleAfterDays ?? DEFAULT_STALE_AFTER_DAYS) * dayMs;

    const observations = orders
        .filter((order) => {
            if (!query.driverId) return true;
            return resolveServiceLearningDriverId(order) === query.driverId;
        })
        .map(extractServiceTimeObservation)
        .filter((observation): observation is ServiceTimeObservation => Boolean(observation));

    const history = observations;
    const last30Days = observations.filter((observation) => (observation.observedAt || 0) > (now - monthMs));
    const today = observations.filter((observation) => (observation.observedAt || 0) > (now - dayMs));
    const fresh = observations.filter((observation) => (observation.observedAt || 0) > (now - freshWindowMs));
    const lastObservationAt = observations.reduce<number | undefined>((latest, current) => {
        if (!current.observedAt) return latest;
        if (!latest) return current.observedAt;
        return current.observedAt > latest ? current.observedAt : latest;
    }, undefined);

    const isStale = !lastObservationAt || (now - lastObservationAt) > staleAfterMs;
    const confidence = computeServiceTimeConfidence(observations.length, fresh.length, isStale);

    return {
        history: averageServiceTime(history),
        last30Days: averageServiceTime(last30Days),
        today: averageServiceTime(today),
        countToday: today.length,
        sampleCount: observations.length,
        freshSampleCount: fresh.length,
        lastObservationAt,
        confidence,
        isStale
    };
};

export const getFleetPerformanceIndex = (drivers: Driver[]): number => {
    const active = drivers.filter(d => d.status !== 'offline');
    if (active.length === 0) return 1.0;
    const avgFactor = active.reduce((sum, d) => sum + (d.speedFactor || 1.0), 0) / active.length;
    return Math.min(1.15, Math.max(0.85, avgFactor));
};
